(ns graph-ui.web.xray
  "X-RAY — every rendered element ringed, and NAMED on its top edge.

  ;; @feature F-graph-ui-v0-010-01-xray-element-names
  ;; @adr ADR-graph-ui-v0-014-01-ui-building-blocks
  ;; @adr ADR-graph-ui-v0-015-01-three-way-sync
  ;; @concept CONCEPT-graph-ui-v0-003-01-widget-tree

  George, 2026-08-17, mid-reader-v2: *\"I want borders on all element, and in
  ideal on the top border the semantic name of the element\"*.

  AN INSTRUMENT, like the ruler beside it — it does not change what you are
  looking at, it tells you what the parts of it are CALLED. The ruler answers
  \"how wide\"; this answers \"which element is that, and where does it come
  from\", which is the question a sentence about a widget has to answer before it
  can be about anything.

  ── IT RINGS, IT DOES NOT BORDER ────────────────────────────────────────────
  The request said `border`, and a border is the one thing this must not use. A
  border participates in layout: 1px on each of 68 nodes nested twelve deep adds
  real inset, and THIS island's whole layout thesis is measures — the reading
  column is `66ch`, the sidebar `34ch`
  (CONCEPT-graph-ui-v0-009-01-measure-the-reading-column). Bordered boxes would
  shift the very geometry the instrument exists to let you read, so it would
  misreport its own subject.

  CSS `outline` is painted outside the box and takes part in no layout at all,
  and the name is an absolutely-positioned pseudo-element, so it has no flow to
  disturb either. Measured 2026-08-17: the reading column is the same width to
  the pixel with x-ray off and on.

  ── WHAT THE NAME IS ────────────────────────────────────────────────────────
  Two different \"semantic names\" sit on every expanded node, and they answer
  different questions. Measured on the live reader-v2 island, 68 widget nodes:

    :import-id   68 distinct — IDENTITY. `reader-v2/collection-title`,
                 `reader-v2/page-kind-label`. Unique per node, and the name you
                 would actually say out loud.
    :from        24 boundaries over 68 nodes — AUTHORED-IN
                 (ADR-graph-ui-v0-015-01 §4): `{:file \"alatus/reading-area.edn\"
                 :part title-row}`, which building-block file the node came from
                 and therefore how many other applications an edit here reaches.

  So the LABEL is the import-id's last segment and the AUTHORED-IN stamp is the hover
  detail. Labelling with `:from` was tried on paper first and rejected: it
  repeats across siblings, so eight nested nodes of the reading area would all
  read `reading-area` and name nothing.

  ── THREE STATES, AND :hover IS THE FIRST ONE ───────────────────────────────
  Off → `:hover` (every ring, the label only under the pointer, ancestors
  included, so the chain reads out) → `:names` (every ring, every label) → off.

  THE ORDER IS A CORRECTION, not a preference. `:names` shipped first and was
  measured only against an EMPTY reader; on a note with 20 blocks George found it
  destroyed the thing it was meant to explain — *\"the element is gone,
  scrambled, overlaped\"*. Two causes, both now fixed or demoted: the plate
  straddled the top edge, so its own bottom border struck through the first line
  of text; and plates of a parent and its first child land on the same pixel,
  which CSS cannot prevent because placement would need a node's DEPTH and
  whether a sibling already claimed that spot.

  `:hover` cannot pile up — it places one chain — so it is what the first click
  gives you. `:names` is still reachable for surveying a sparse island, and the
  button's tooltip says it is crowded before you get there.

  ── THE ONE COST, STATED ────────────────────────────────────────────────────
  Turning it on gives every node the same wrapper div `?instrument=1` gives it
  (render/render-node), so a toggle REMOUNTS the tree — a half-typed input
  buffer does not survive it. And the wrapper carries a `title`, so while x-ray
  is on it shadows a widget's own tooltip. Both are the price of an inspector
  that covers every node instead of only the ones that happen to be wrapped
  already; neither is paid with x-ray off, where the render is byte-identical to
  what it was before this namespace existed."
  (:require [reagent.core :as r]
            [clojure.string :as str]
            [cljs.reader :as reader]))

;; ---------------------------------------------------------------------------
;; The naming — pure, so it is tested without a browser
;; ---------------------------------------------------------------------------

(defn- last-segment
  "`reader-v2/collection-title` → `collection-title`.

  The prefix is the island's own name, repeated on all 68 nodes, so it is the
  one part of the id that distinguishes nothing. Dropped here and kept in the
  hover detail, where there is room for it."
  [s]
  (let [s (str s)]
    (if-let [i (str/last-index-of s "/")] (subs s (inc i)) s)))

(defn- block-name
  "`alatus/reading-area.edn` → `alatus/reading-area`. The product prefix STAYS:
  `alatus/` versus `ikc/` is the difference between a product block and a
  platform one, which is exactly what a reader wants to know about a region."
  [file]
  (str/replace (str file) #"\.edn$" ""))

(defn element-name
  "The short name for one render-tree node — what goes on its top edge.

  Precedence, most specific first. Every arm is reached by real data on the
  live island: the first by all 68 expanded nodes, the second and third by a
  node from a description that predates the block form, the last by anything
  hand-built with neither."
  [{:keys [import-id from]} kind]
  (or (when (seq (str import-id)) (last-segment import-id))
      (when-let [p (:part from)] (str p))
      (when-let [f (:file from)] (block-name f))
      (some-> kind name)
      "?"))

(defn element-detail
  "The hover line: identity, kind, and where the node came FROM.

  `:repeated` is carried when a node was produced per-item by a page's
  collection (expand stamps it), and it is the difference between 'this is the
  collection section' and 'this is one of them' — invisible in the tree shape,
  so it is said here."
  [{:keys [import-id from widget-id]} kind]
  (let [{:keys [file part repeated]} from]
    (str/join
     "\n"
     (remove nil?
             [(str (or import-id (some-> kind name)) (when kind (str "  ·  :" (name kind))))
              (when file (str "from " (block-name file)
                              (when part (str "  ·  " part))
                              (when repeated (str "  ·  repeated per " repeated))))
              (when widget-id (str "widget-id " widget-id))]))))

;; ---------------------------------------------------------------------------
;; PLACEMENT — pure, and the reason this feature works at all
;;
;; Two CSS attempts failed here before the problem was named properly. A
;; stylesheet places each label knowing ONLY that label's own element: not how
;; deep it sits, not whether a sibling already claimed the pixel. Since a parent
;; and its first child share a top edge constantly in flex-column layouts, their
;; plates overprinted — and overprinting was guaranteed by the MEDIUM, not by a
;; bad offset. Nudging it (straddle → above) just moved the collision.
;;
;; Once the geometry is measured, "does this label overlap one already placed?"
;; is decidable. So it is decided here, in a pure function, tested headlessly.
;; ---------------------------------------------------------------------------

(def ^:private label-h
  "Plate height in px. 12 carries 8px monospace with a pixel of air."
  12)

(defn label-width
  "Plate width in px for a name, ESTIMATED rather than measured.

  8px monospace advances ~4.9px per character; 6px covers the two 3px pads. An
  estimate is honest here because it is only ever used to decide overlap, and it
  errs WIDE — a slightly-too-wide box rejects a marginal label instead of letting
  two touch, which is the failure direction that matters."
  [name]
  (+ 6 (js/Math.ceil (* 4.9 (count (str name))))))

(defn- overlaps?
  "Do two axis-aligned boxes share any area? Touching edges do not count."
  [a b]
  (and (< (:x a) (+ (:x b) (:w b)))
       (< (:x b) (+ (:x a) (:w a)))
       (< (:y a) (+ (:y b) (:h b)))
       (< (:y b) (+ (:y a) (:h a)))))

(defn place-labels
  "Decide which elements get a visible name, and where.

  `items` is `[{:name … :rect {:x :y :w :h}} …]` in DOCUMENT ORDER, which is
  outer-first — so a parent wins the spot over its own first child. That is the
  right precedence: the parent names the larger region, and the child is one
  hover away.

  Returns the same maps, each with `:label {:x :y :w :h}` when a plate fits, and
  without it when one does not. A skipped label still keeps its ring, so the
  element is never invisible — only unnamed.

  Three reasons to skip, in order of how often they fire:
    - the plate would overlap one already placed;
    - the element is too small to carry a name honestly (`min-w`/`min-h`);
    - the plate would land above the viewport, where it cannot be read.

  `above?` false places the plate INSIDE the element's top-left instead of above
  it — used for the topmost row of elements, whose plate would otherwise be
  clipped away."
  [items & {:keys [min-w min-h] :or {min-w 30 min-h 13}}]
  (loop [[it & more] items, placed [], out []]
    (if (nil? it)
      out
      (let [{:keys [x y w h]} (:rect it)
            lw    (label-width (:name it))
            above {:x x :y (- y label-h) :w lw :h label-h}
            ;; Clipped off the top of the window? Tuck it inside the element
            ;; instead of dropping the name entirely.
            box   (if (neg? (:y above)) (assoc above :y y) above)
            fits? (and (>= w min-w) (>= h min-h)
                       (not-any? #(overlaps? box %) placed))]
        (recur more
               (if fits? (conj placed box) placed)
               (conj out (cond-> it fits? (assoc :label box))))))))

(defn label-box
  "Where one item's plate goes, ignoring every other item.

  Split out from `place-labels` because HOVER must not go through placement: the
  pointer names whatever it is on, including a caret or an icon button that
  `place-labels` skipped for being too small or for colliding. Routing hover
  through the placed set meant pointing at a small thing named nothing — the
  instrument silent about exactly the element you had to ask about."
  [{:keys [name rect]}]
  (let [{:keys [x y]} rect
        b {:x x :y (- y label-h) :w (label-width name) :h label-h}]
    (if (neg? (:y b)) (assoc b :y y) b)))

(defn innermost-at
  "The measured item under a point — the SMALLEST-area rect containing it.

  Used for the hover highlight, in place of walking the DOM with
  `elementFromPoint`: the rects are already measured this frame, and 'smallest
  containing box' is what 'innermost' means once you have them."
  [items px py]
  (->> items
       (filter (fn [{{:keys [x y w h]} :rect}]
                 (and (>= px x) (<= px (+ x w)) (>= py y) (<= py (+ y h)))))
       (sort-by (fn [{{:keys [w h]} :rect}] (* w h)))
       first))

;; ---------------------------------------------------------------------------
;; State — off, named, or named-on-hover; remembered across reloads
;; ---------------------------------------------------------------------------

(def modes
  "The cycle. `nil` is off and is not in the vector — a cycle that could not
  return to off would make the instrument chrome.

  :HOVER COMES FIRST, and that order was decided by the defect. The first version
  put :names first, and on a real page — a note with 20 blocks, not the empty
  reader the first probe measured — George found it unusable: *\"instead of
  helping to know what the element is, the element is gone, scrambled,
  overlaped\"*. 68 plates on a tree twelve deep collide with each other and with
  the content, and no CSS selector can prevent it: placement would need to know a
  node's DEPTH and whether a sibling already claimed that pixel, and a stylesheet
  knows neither. :hover only ever places one ancestor chain, so it cannot pile
  up. :names is kept, second, because a sparse island can be surveyed with it."
  [:hover :names])

(def ^:private storage-key "ikc3.xray")

(defn- persist! [{:keys [mode]}]
  (try (.setItem js/localStorage storage-key (pr-str {:mode mode}))
       (catch :default _ nil)))

(defn- from-url
  "?xray=1 → :hover (\"on\" means what one click gives you), ?xray=names → all
  names at once, ?xray=hover → the same as 1, ?xray=0 → explicitly off.

  Present so a headless run can ask for the instrument without a click — the
  same seam `?instrument=1` gives the fidelity measurement — and so a screenshot
  in a plan can be reproduced from its URL alone.

  GUARDED, because this is read at namespace load and there is a real host with
  no `location`: the Node runner behind `devbox run test-cljs`. Unguarded it
  threw on import, which fails the whole CLJS suite from a namespace none of it
  is about — the same shape as a broken `dev/user.clj` blocking the Clojure
  suite with no corresponding diff."
  []
  (try
    (case (some-> (js/URLSearchParams. (.-search js/location)) (.get "xray"))
      "1"     {:mode :hover}
      "hover" {:mode :hover}
      "names" {:mode :names}
      "0"     {:mode nil}
      nil)
    (catch :default _ nil)))

(defn- restore
  "The stored mode, refusing a stale or unknown one the same way
  theme/set-theme! refuses a stored key it no longer recognises."
  []
  (try (let [m (some-> (.getItem js/localStorage storage-key) reader/read-string)]
         (when (and (map? m) (some #{(:mode m)} modes))
           (select-keys m [:mode])))
       (catch :default _ nil)))

(defonce state
  ;; URL LAST, so it wins: an explicit ?xray= in the address bar is a stronger
  ;; statement about this page load than what the last one happened to leave in
  ;; localStorage.
  (r/atom (merge {:mode nil} (restore) (from-url))))

(defn mode
  "The live mode, as a reactive read — a component calling this re-renders when
  the instrument is toggled, which is what makes the stamp in render/render-node
  appear and disappear without any other wiring."
  []
  (:mode @state))

(defn on?
  "Is the instrument stamping at all? The one predicate render-node needs."
  []
  (boolean (mode)))

(defn toggle!
  "off → :hover → :names → off. See `modes` for why :hover is first."
  []
  (persist!
   (swap! state update :mode
          (fn [m] (case m
                    nil    :hover
                    :hover :names
                    nil)))))

;; ---------------------------------------------------------------------------
;; THE OVERLAY — the drawing, moved OUT of the inspected tree
;;
;; Everything above the ruler line was once done in CSS on each node's own
;; wrapper (`position: relative` plus a `::before` plate) and it could not be
;; made to work: see `place-labels`. Drawn here instead, in one fixed layer over
;; the app, which buys four things at once —
;;
;;   * labels can be placed with COLLISION AVOIDANCE, the thing CSS could not do;
;;   * nothing is added to any inspected element, so no `position: relative`
;;     re-parents a Mantine popover and no `title` shadows a widget's own tooltip;
;;   * a plate can be SKIPPED when it does not fit, leaving the ring;
;;   * the ring is drawn from a rect measured this frame, so it cannot drift.
;;
;; RE-MEASURED EVERY FRAME while on, which is the one thing an overlay must get
;; right — a stale overlay draws rings beside their elements, which is worse than
;; the CSS version it replaces. 68 `getBoundingClientRect` calls per frame is
;; nothing, and the atom is only reset when the geometry actually CHANGED, so a
;; still page costs one measurement pass and no re-render.
;; ---------------------------------------------------------------------------

(defonce ^:private measured (r/atom {:items [] :mouse nil}))

(defn- own-or-children-rect
  "The viewport rect to ring for a stamped element.

  MOST stamps sit on a `display: contents` marker, which by definition generates
  no box — `getBoundingClientRect` on it is 0x0. So the rect comes from the
  element's CHILDREN, unioned. That is not a fallback but the normal path: it is
  what keeps x-ray from having a box of its own to disturb the layout with
  (render-node's `marker?`).

  Unioned rather than taking the first child, because a widget may render several
  roots and ringing only the first would understate the element."
  [el]
  (let [r (.getBoundingClientRect el)]
    (if (and (pos? (.-width r)) (pos? (.-height r)))
      {:x (.-left r) :y (.-top r) :w (.-width r) :h (.-height r)}
      (let [kids (.-children el)
            n    (.-length kids)]
        (loop [i 0, x1 nil, y1 nil, x2 nil, y2 nil]
          (if (>= i n)
            (when (and x1 (> x2 x1) (> y2 y1))
              {:x x1 :y y1 :w (- x2 x1) :h (- y2 y1)})
            (let [kr (.getBoundingClientRect (.item kids i))]
              (if (and (pos? (.-width kr)) (pos? (.-height kr)))
                (recur (inc i)
                       (if x1 (min x1 (.-left kr))   (.-left kr))
                       (if y1 (min y1 (.-top kr))    (.-top kr))
                       (if x2 (max x2 (.-right kr))  (.-right kr))
                       (if y2 (max y2 (.-bottom kr)) (.-bottom kr)))
                (recur (inc i) x1 y1 x2 y2)))))))))

(defn- measure-items
  "Every stamped element's name, detail and viewport rect, in document order
  (outer-first — `place-labels` depends on that ordering)."
  []
  (let [nodes (.querySelectorAll js/document "[data-xray-name]")]
    (into []
          (for [i (range (.-length nodes))
                :let [el   (.item nodes i)
                      rect (own-or-children-rect el)]
                ;; Nothing measurable means nothing to ring and no room for a
                ;; name — an element rendering no visible box at all.
                :when rect]
            {:name   (.getAttribute el "data-xray-name")
             :detail (.getAttribute el "data-xray-detail")
             :rect   rect}))))

(defonce ^:private raf-id (atom nil))

(defn- tick! []
  (when (on?)
    (let [items (measure-items)]
      ;; Only write when something moved. reset! notifies unconditionally, so an
      ;; unguarded write would re-render the overlay 60 times a second over a
      ;; page nobody is touching.
      (when (not= items (:items @measured))
        (swap! measured assoc :items items))))
  (reset! raf-id (js/requestAnimationFrame tick!)))

(defn- on-mouse-move [e]
  (swap! measured assoc :mouse {:x (.-clientX e) :y (.-clientY e)}))

(defn overlay
  "The instrument's whole visible surface. Mounted once, beside the render tree;
  renders nothing at all while the mode is off."
  []
  (r/create-class
   {:display-name "xray-overlay"
    :component-did-mount
    (fn [_]
      (.addEventListener js/document "mousemove" on-mouse-move true)
      (tick!))
    :component-will-unmount
    (fn [_]
      (.removeEventListener js/document "mousemove" on-mouse-move true)
      (some-> @raf-id js/cancelAnimationFrame))
    :reagent-render
    (fn []
      (let [m                     (mode)
            {:keys [items mouse]} @measured]
        (when (and m (seq items))
          (let [placed (place-labels items)
                hot    (when mouse (innermost-at items (:x mouse) (:y mouse)))
                hot?   (fn [it] (and hot (= (:rect it) (:rect hot))
                                     (= (:name it) (:name hot))))
                ;; :names shows every plate that FITS; :hover shows none of them.
                ;; Either way every ring is drawn — "borders on all element" was
                ;; the ask, and it is the half that always reads cleanly.
                show?  (fn [it] (and (= m :names) (some? (:label it)) (not (hot? it))))]
            [:div {:style {:position "fixed" :inset 0
                           :pointer-events "none"
                           ;; --layer-instrument, NOT --layer-overlay. Measured:
                           ;; the AppShell navbar computes to z-index 101 and
                           ;; mantine-core.css reaches 1001, so at 40 the sidebar
                           ;; painted OVER the instrument — the sidebar appearing
                           ;; to "fight it", and the hover panel showing only a
                           ;; sliver past the sidebar's right edge.
                           :z-index "var(--layer-instrument, 2000)"}}
             ;; rings
             (into [:<>]
                   (for [[i {:keys [rect] :as it}] (map-indexed vector placed)]
                     ^{:key (str "r" i)}
                     [:div {:style {:position "absolute"
                                    :left (str (:x rect) "px") :top (str (:y rect) "px")
                                    :width (str (:w rect) "px") :height (str (:h rect) "px")
                                    :outline (str "1px solid var(--"
                                                  (if (hot? it) "accent" "muted") ")")
                                    :outline-offset "-1px"}}]))
             ;; plates — the placed ones, in :names only
             (let [plate (fn [box nm accent?]
                           [:div {:style {:position "absolute"
                                          :left (str (:x box) "px") :top (str (:y box) "px")
                                          :height (str label-h "px")
                                          :line-height (str label-h "px")
                                          :padding "0 3px"
                                          :font-family "var(--font-mono)" :font-size "8px"
                                          :letter-spacing "0.02em" :white-space "nowrap"
                                          :color (if accent? "var(--accent)" "var(--muted)")
                                          :background (if accent? "var(--accent-soft)" "var(--panel)")
                                          :outline (str "1px solid var(--"
                                                        (if accent? "accent" "border-2") ")")}}
                            nm])]
               [:<>
                (into [:<>]
                      (for [[i {:keys [label name] :as it}] (map-indexed vector placed)
                            :when (show? it)]
                        ^{:key (str "l" i)} (plate label name false)))
                ;; …and the HOVERED one, always, drawn last so it wins the pixel.
                ;; Its box is computed directly rather than taken from `placed`:
                ;; the pointer must name a caret or an icon button too, and those
                ;; are exactly what placement skips.
                (when hot
                  ^{:key "hot"} (plate (label-box hot) (:name hot) true))])
             ;; The hovered element's full authored-in stamp, as a STATUS STRIP along the
             ;; bottom edge.
             ;;
             ;; It was a box pinned at bottom-LEFT, which is where the 34ch sidebar
             ;; is: the two shared a corner, and George saw the panel apparently
             ;; underneath the sidebar with only a fragment ("ection-", the tail of
             ;; a part name) visible past its right edge. A panel that describes the
             ;; interface must not sit on top of it.
             ;;
             ;; Full width and one line high cannot collide horizontally with
             ;; anything, and costs a strip of a surface that has no content at its
             ;; very bottom edge — the same reasoning as the top-bar ruler, which is
             ;; a strip for the same reason. Newlines are joined to " · " so the
             ;; height is fixed no matter how long a node's authored-in stamp is.
             (when-let [d (:detail hot)]
               [:div {:style {:position "absolute" :left 0 :right 0 :bottom 0
                              :padding "3px 8px"
                              :font-family "var(--font-mono)" :font-size "10px"
                              :line-height "14px" :white-space "nowrap"
                              :overflow "hidden" :text-overflow "ellipsis"
                              :color "var(--text)" :background "var(--panel)"
                              :border-top "1px solid var(--muted)"}}
                (str/replace d #"\n" "   ·   ")])]))))}))

(defn label
  "What the top-bar button's tooltip says, naming the state it is IN and the one
  a click moves it to — a three-state control that only says its own name leaves
  the third state undiscoverable."
  []
  (case (mode)
    nil    "X-ray: off — click to ring every element and name the one under the pointer"
    :hover "X-ray: every element ringed, name under the pointer — click to name ALL of them (crowded on a dense page)"
    :names "X-ray: every element named at once — click to turn off"))
